/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {Box, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import Render from '@ion/dls/src/render/Render';
import {
  SentinelDeviceType,
  SentinelTabType,
  style
} from './SentinelAssociationTab';
import {setSecondaryDevice} from './state';

interface SentinelTabProps {
  setActiveTabId?: (activateTabId: SentinelTabType) => void;
  currentTabIdentifier?: string;
  activeTabId?: SentinelTabType;
  deviceData?: GenericObject;
  isSecondarySilent?: boolean;
};

interface StyleProps {
  isActive?: boolean;
  isSecondaryDevice?: boolean;
};

const useStyles = makeStyles(() => {
  return {
    sentinelDeviceStyle : {
      ...style,
      color      : ({isActive}: StyleProps) => isActive ? 'rgba(51, 51, 51, 1)' : 'rgba(130, 130, 130, 1)',
      fontSize   : '24px !important',
      lineHeight : '28px',
      mb         : 1
    },
    sentinelIdentifierStyle : {
      ...style,
      color          : ({isActive}: StyleProps) => isActive ? 'rgba(51, 51, 51, 1)' : 'rgba(130, 130, 130, 1)',
      textDecoration : ({isSecondaryDevice}: StyleProps) => isSecondaryDevice ? 'underline' : 'none',
      fontSize       : '16px !important',
      lineHeight     : '25px'
    },
    sentinelWarningStyle : {
      color      : 'red',
      marginLeft : 8,
      display    : 'inline-flex',
      alignItems : 'center'
    },
    sentinelWarningTextStyle : {
      fontFamily    : 'Roboto !important',
      fontWeight    : 500,
      letterSpacing : '0.5px',
      verticalAlign : 'middle',
      color         : 'rgba(130, 130, 130, 1)',
      fontSize      : '9px',
      lineHeight    : '16px',
      textTransform : 'uppercase'
    }
  };
});

const commonTabStyles = (isActive) => ({
  backgroundColor      : isActive ? 'white' : 'rgba(242, 242, 242, 1)',
  borderTopLeftRadius  : isActive ? '8px' : {xs: 8, sm: 0},
  borderTopRightRadius : isActive ? '8px' : {xs: 8, sm: 0},
  transform            : isActive ? 'translateY(1px)' : 'none',
  zIndex               : isActive ? 2 : 1,
  mb                   : {xs: isActive ? 0 : 0.5, sm: 0},
  '&:hover'            : {
    backgroundColor : isActive ? 'white' : 'rgba(242, 242, 242, 1)'
  },
  p              : 1.5,
  height         : '94px',
  width          : '227px',
  cursor         : 'pointer',
  position       : 'relative',
  display        : 'flex',
  flexDirection  : 'column',
  justifyContent : 'center'
});

/**
 * Displays a tab for a Sentinel device association (primary or secondary),
 * including device title and identifier. Handles tab switching and (optionally)
 * dispatches actions for secondary device selection.
 * 
 * ```
 * 
 * <SentinelTab
 *   deviceData={tabData}
 *   activeTabId={activeTab}
 *   setActiveTabId={setActiveTab}
 *   currentTabIdentifier={identifier}
 * />
 * 
 * ```
 * 
 */
const SentinelTab = ({
  deviceData,
  activeTabId,
  setActiveTabId,
  currentTabIdentifier,
  isSecondarySilent = false
}: SentinelTabProps) => {
  const dispatch          = useDispatch();
  const isActive          = isEqual(activeTabId, deviceData?.tabId);
  const hasOtherDevice    = isEqual(deviceData?.tabId, SentinelTabType.OTHER_DEVICE);
  const isSecondaryDevice = isEqual(deviceData?.deviceType, SentinelDeviceType.SECONDARY) && hasOtherDevice;
  const classes           = useStyles({isActive, isSecondaryDevice});

  const handleIdentifierClick = useCallback(async() => {
    if (isSecondaryDevice && hasOtherDevice && isActive) {
      dispatch(setSecondaryDevice(deviceData?.identifier, true));
    }
  }, [isSecondaryDevice, hasOtherDevice, isActive, deviceData]);

  return (
    <Render.If condition={deviceData?.identifier}>
      <Box onClick={function() { setActiveTabId(deviceData?.tabId); }}
        sx={commonTabStyles(isActive)}>
        <Typography className={classes.sentinelDeviceStyle}>
          {deviceData?.title}
        </Typography>
        <Typography className={classes.sentinelIdentifierStyle} onClick={handleIdentifierClick}>
          {currentTabIdentifier} : {deviceData?.identifier}
          <Render.If condition={isSecondarySilent}>
            <span className={classes.sentinelWarningStyle}>
              <Tooltip arrow
                disableHoverListener={!isActive}
                title={translate('admin-devices:view.sentinel.warningInfo')}>
                <span>
                  {deviceData?.warningIcon}
                </span>
              </Tooltip>
            </span>
          </Render.If>
        </Typography>
        <Render.If condition={!isActive && isSecondaryDevice && isSecondarySilent}>
          <span className={classes.sentinelWarningTextStyle}>
            {translate('admin-devices:view.sentinel.warningInfo')}
          </span>
        </Render.If>
      </Box>
    </Render.If>
  );
};

export default React.memo(SentinelTab);
